import { useMediaQuery } from "@mui/material";
import { useRouter } from "next/router";
import posthog from "posthog-js";
import querystring from "querystring";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Color from "../../../assets/colors";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import useTriggerSnackbar from "../../../hooks/useTriggerSnackbar";
import { updateContactUsEmployer } from "../../../redux/actions/dialog_action";
import { submitContactUs } from "../../../redux/actions/user_action";
import {
  BoxContainer,
  ButtonStyled,
  CallIcon,
  CancelButtonStyled,
  CardContainer,
  CheckStyled,
  CheckboxStyled,
  CheckedIcon,
  CloseButton,
  CloseIcon,
  DialogContainer,
  EmailIcon,
  OptionStyled,
  PhoneInputStyled,
  ScrollContainer,
  SelectStyled,
  TextFieldStyled,
  TextStyled,
  UncheckedIcon,
} from "./styles";

function EmployerLandingContactUsDialog(props) {
  const isDesktop = useMediaQuery("(min-width:1280px)");
  const isTablet = useMediaQuery("(min-width:640px)");

  const router = useRouter();
  const dispatch = useDispatch();
  const [triggerSnackbarFunc] = useTriggerSnackbar();

  const visible = useSelector(
    (state) => state?.dialog?.showContactUsEmployerDialog
  );

  const initialValues = {
    name: {
      value: "",
      error: false,
    },
    company: {
      value: "",
      error: false,
    },
    email: {
      value: "",
      error: false,
      invalid: false,
    },
    phone: {
      value: "",
      error: false,
      invalid: false,
    },
    services: {
      value: [],
      error: false,
    },
    heardFrom: { value: "", error: false },
    additionalDetails: {
      value: "",
    },
  };

  const [formFields, setFormFields] = useState(initialValues);

  useEffect(() => {
    if (visible) {
      setFormFields((prev) => ({
        ...prev,
        services: {
          ...prev.services,
          value: [visible?.prefilled],
        },
        additionalDetails: {
          ...prev.additionalDetails,
          value: visible?.message,
        },
      }));
    }
  }, [visible]);

  let textFieldList = [
    {
      label: "Name*",
      placeholder: "Name",
      name: "name",
      errorMessage: "Name is required",
      required: true,
      type: "text",
    },
    {
      label: "Company Name*",
      placeholder: "Company",
      name: "company",
      errorMessage: "Company name is required",
      required: true,
      type: "text",
    },
    {
      label: "Email*",
      placeholder: "Email",
      name: "email",
      errorMessage: "Email is required",
      invalidMessage: "Invalid Email",
      required: true,
      type: "email",
    },
    {
      label: "Contact Number*",
      placeholder: "Phone",
      name: "phone",
      errorMessage: "Phone Number is required",
      invalidMessage: "Invalid Phone Number",
      required: true,
      type: "phone",
    },
  ];

  const additionalDetailsItem = {
    name: "additionalDetails",
    label: "Any additional details you would like us to know? (optional)",
    placeholder: "Let us know any details you want to know more about.",
    required: false,
    type: "textarea",
  };

  const checkboxList = [
    { label: "Job Posting", value: "Job Posting" },
    { label: "Employer Branding", value: "Employer Branding" },
    { label: "Headhunting", value: "Headhunting" },
  ];

  const dropdownList = [
    {
      value: "social media",
      label: "Social Media",
    },
    {
      value: "search engine",
      label: "Search Engine (Google, Bing, etc.)",
    },
    {
      value: "newspaper",
      label: "Newspaper/Online Newspaper",
    },
    {
      value: "referral",
      label: "Referral",
    },
    {
      value: "internet advertisement",
      label: "Internet Advertisement",
    },
    {
      value: "others",
      label: "Other",
    },
  ];

  function handleClose() {
    setFormFields(initialValues);
    dispatch(updateContactUsEmployer(false));
  }

  function handleSubmit() {
    if (
      formFields?.name?.value?.length === 0 ||
      formFields?.company?.value?.length === 0 ||
      formFields?.email?.value?.length === 0 ||
      formFields?.phone?.value?.length === 0 ||
      formFields?.services?.value?.length === 0 ||
      formFields?.heardFrom?.value?.length === 0
    ) {
      let updatedFormFields = { ...formFields };

      if (formFields?.name?.value?.length === 0) {
        updatedFormFields = {
          ...updatedFormFields,
          name: {
            value: formFields?.name?.value,
            error: true,
          },
        };
      }

      if (formFields?.company?.value?.length === 0) {
        updatedFormFields = {
          ...updatedFormFields,
          company: {
            value: formFields?.company?.value,
            error: true,
          },
        };
      }

      if (formFields?.email?.value?.length === 0) {
        updatedFormFields = {
          ...updatedFormFields,
          email: {
            value: formFields?.email?.value,
            error: true,
          },
        };
      }

      if (formFields?.phone?.value?.length === 0) {
        updatedFormFields = {
          ...updatedFormFields,
          phone: {
            value: formFields?.phone?.value,
            error: true,
          },
        };
      }

      if (formFields?.services?.value?.length === 0) {
        updatedFormFields = {
          ...updatedFormFields,
          services: {
            value: formFields?.services?.value,
            error: true,
          },
        };
      }

      if (formFields?.heardFrom?.value?.length === 0) {
        updatedFormFields = {
          ...updatedFormFields,
          heardFrom: {
            value: formFields?.heardFrom?.value,
            error: true,
          },
        };
      }

      setFormFields(updatedFormFields);
      return;
    }

    const trackingParameters = querystring.stringify(router.query);

    let params = {
      name: formFields?.name?.value,
      companyName: formFields?.company?.value,
      email: formFields?.email?.value,
      phone: formFields?.phone?.value,
      services: formFields?.services?.value.filter(Boolean),
      message: formFields?.additionalDetails?.value,
      heardFrom: formFields?.heardFrom?.value,
      origin: "employer_landing",
      canCancel: false,
      trackingParameters: trackingParameters,
    };

    dispatch(submitContactUs(params)).then((res) => {
      if (res?.type === "SUBMIT_CONTACT_US_SUCCEED") {
        triggerSnackbarFunc({
          snackbarMessage: "Feedback submitted successfully!",
          severity: "success",
        });
      } else {
        triggerSnackbarFunc({
          snackbarMessage:
            "Failed to submit feedback at the moment, please try again later.",
          severity: "error",
        });
      }
    });

    const origin = visible?.origin;
    switch (origin) {
      case "head-banner":
        sendTrackingEvent({
          event: "CE_contact-us-head-banner-submitted",
        });
        posthog.capture(
          //posthog tracking (Contact Us Submit button) [JSW-3049]
          "elp_contact_us_submit",
          {
            section: "hero",
            cta: "Submit",
            colour: "black",
            awareness_source: formFields?.heardFrom?.value,
            service_interested: formFields?.services?.value,
          }
        );
        sendTrackingEvent(
          //tracking (Contact Us Submit button) [JSW-3049]
          {
            event: "CE_elp_contact_us_submit",
            section: "hero",
            cta: "Submit",
            colour: "black",
            awareness_source: formFields?.heardFrom?.value,
            service_interested: formFields?.services?.value,
          }
        );
        break;
      case "interested-know-more":
        sendTrackingEvent({
          event: "CE_contact-us-middle-banner-submitted",
        });
        break;
      case "contact-us-banner":
        sendTrackingEvent({
          event: "CE_contact-us-foot-banner-submitted",
        });
        posthog.capture(
          //posthog tracking (Contact Us Submit button) [JSW-3049]
          "elp_contact_us_submit",
          {
            section: "footer",
            cta: "Submit",
            colour: "black",
            awareness_source: formFields?.heardFrom?.value,
            service_interested: formFields?.services?.value,
          }
        );
        sendTrackingEvent(
          //tracking (Contact Us Submit button) [JSW-3049]
          {
            event: "CE_elp_contact_us_submit",
            section: "footer",
            cta: "Submit",
            colour: "black",
            awareness_source: formFields?.heardFrom?.value,
            service_interested: formFields?.services?.value,
          }
        );
        break;
      default:
        break;
    }

    setFormFields(initialValues);
    dispatch(updateContactUsEmployer(false));
  }

  return (
    <DialogContainer
      onClose={() => handleClose(false)}
      open={visible?.state ? visible?.state : visible}
    >
      <CardContainer>
        <BoxContainer
          padding={"1rem 1rem 1rem 1.5rem"}
          justifyContent={"space-between"}
          borderBottom={"1px solid" + Color.grey}
        >
          <TextStyled fontSize={"1rem"} fontWeight={700}>
            Hi Employers!
          </TextStyled>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </BoxContainer>

        <ScrollContainer flexDirection={"column"}>
          <BoxContainer
            flexDirection={"column"}
            gap={"1.5rem"}
            padding={"1.5rem"}
          >
            <BoxContainer
              flexDirection={"column"}
              justifyContent={"space-between"}
              gap={"1rem"}
              // height={isTablet ? "100%" : "20.375rem"}
            >
              <BoxContainer
                justifyContent={"space-between"}
                flexWrap={"wrap"}
                gap={"0.75rem"}
              >
                {textFieldList.map((item, index) => {
                  return (
                    <TextFieldInput
                      key={index}
                      item={item}
                      formFields={formFields}
                      setFormFields={setFormFields}
                    />
                  );
                })}
              </BoxContainer>
            </BoxContainer>

            <BoxContainer
              flexDirection={"column"}
              gap={"0.75rem"}
              // height={isTablet ? "100%" : "9.25rem"}
            >
              <TextStyled fontSize={"14px"}>
                Which services are you interested in?*
              </TextStyled>
              <BoxContainer
                flexDirection={isTablet ? "row" : "column"}
                gap={isTablet ? "0.5rem" : "0.625rem"}
                justifyContent={"space-between"}
              >
                {checkboxList.map((item, index) => {
                  return (
                    <CheckBoxInput
                      key={index}
                      label={item.label}
                      value={item.value}
                      formFields={formFields}
                      setFormFields={setFormFields}
                    />
                  );
                })}
              </BoxContainer>
              {formFields?.services?.error && (
                <TextStyled
                  color={Color.error}
                  marginLeft={"1rem"}
                  fontSize={"12px"}
                >
                  Please select at least one services
                </TextStyled>
              )}
            </BoxContainer>

            <BoxContainer flexDirection={"column"} gap={"0.5rem"}>
              <TextStyled fontSize={"14px"}>
                How did you hear about us?*
              </TextStyled>
              <DropDownInput
                dropdownList={dropdownList}
                formFields={formFields}
                setFormFields={setFormFields}
              />
              {formFields?.heardFrom?.error && (
                <TextStyled
                  color={Color.error}
                  marginLeft={"1rem"}
                  fontSize={"12px"}
                >
                  Please select at least one option
                </TextStyled>
              )}
            </BoxContainer>

            <TextFieldInput
              item={additionalDetailsItem}
              formFields={formFields}
              setFormFields={setFormFields}
            />
          </BoxContainer>

          {/* <BoxContainer
            margin={isDesktop && "0 3.375rem"}
            borderTop={"1px solid" + Color.lightGrey}
          ></BoxContainer> */}

          <BoxContainer
            flexDirection={"column"}
            gap={"0.5rem"}
            padding={"1.5rem"}
          >
            <TextStyled fontSize={"14px"}>
              You can also reach out to us via:
            </TextStyled>
            <BoxContainer
              bgcolor={Color.lightPurple}
              flexDirection={"column"}
              gap={"0.5rem"}
              padding={"1rem"}
              borderRadius={"0.5rem"}
            >
              <BoxContainer gap={"0.5rem"} alignItems={"center"}>
                <CallIcon />
                <TextStyled fontSize={isTablet ? "0.875rem" : "0.75rem"}>
                  Call us:
                </TextStyled>
                <TextStyled
                  color={Color.hiredlyPurple}
                  fontSize={isTablet ? "0.875rem" : "0.75rem"}
                >
                  +603 2771 0141
                </TextStyled>
              </BoxContainer>
              <BoxContainer gap={"0.5rem"} alignItems={"center"}>
                <EmailIcon />
                <TextStyled fontSize={isTablet ? "0.875rem" : "0.75rem"}>
                  Email us:
                </TextStyled>
                <TextStyled
                  color={Color.hiredlyPurple}
                  fontSize={isTablet ? "0.875rem" : "0.75rem"}
                >
                  employer@hiredly.com
                </TextStyled>
              </BoxContainer>
            </BoxContainer>
          </BoxContainer>
        </ScrollContainer>

        <BoxContainer
          justifyContent={"flex-end"}
          padding={"1rem"}
          borderTop={"1px solid" + Color.grey}
          gap={"16px"}
        >
          <CancelButtonStyled onClick={() => handleClose(false)}>
            Cancel
          </CancelButtonStyled>
          <ButtonStyled onClick={handleSubmit}>Submit Form</ButtonStyled>
        </BoxContainer>
      </CardContainer>
    </DialogContainer>
  );
}

function TextFieldInput(props) {
  const { item, formFields, setFormFields } = props;

  const isTablet = useMediaQuery("(min-width:640px)");

  function handleChange(e) {
    const { name, value } = e?.target;

    if (value?.length <= 1) {
      setFormFields({
        ...formFields,
        [name]: {
          error: true,
        },
      });
      return;
    }

    if (name === "email") {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex?.test(value)) {
        setFormFields({
          ...formFields,
          email: {
            invalid: true,
          },
        });
        return;
      }
    }

    setFormFields({
      ...formFields,
      [name]: {
        value: value,
      },
    });
  }

  function handlePhoneChange(value) {
    if (value?.length === 0 || value === undefined) {
      setFormFields({
        ...formFields,
        phone: {
          error: true,
        },
      });
      return;
    }

    if (value?.length < 8 || value?.length > 15) {
      setFormFields({
        ...formFields,
        phone: {
          invalid: true,
        },
      });
      return;
    }

    setFormFields({
      ...formFields,
      phone: {
        value: value,
      },
    });
  }

  return (
    <BoxContainer
      flexDirection={"column"}
      gap={"0.5rem"}
      sx={item?.type !== "textarea" && isTablet && { width: "268px" }}
    >
      <TextStyled fontSize={"14px"}>{item?.label}</TextStyled>
      {item?.type === "phone" ? (
        <>
          <PhoneInputStyled
            className="custom-phone-input"
            defaultCountry="MY"
            countryCallingCodeEditable={false}
            international
            onChange={handlePhoneChange}
            error={
              formFields?.[item?.name]?.error || formFields?.phone?.invalid
            }
          />
        </>
      ) : item?.type === "textarea" ? (
        <TextFieldStyled
          type={item?.type}
          name={item?.name}
          value={formFields?.additionalDetails.value}
          placeholder={item?.placeholder}
          multiline
          rows={9}
          onChange={handleChange}
        />
      ) : (
        <TextFieldStyled
          type={item?.type}
          name={item?.name}
          placeholder={item?.placeholder}
          required={item?.required}
          error={
            formFields?.[item?.name]?.error ||
            (item?.type === "email" && formFields?.email?.invalid)
          }
          onChange={handleChange}
          sx={{
            "& .MuiOutlinedInput-root": {
              maxHeight: isTablet ? "3rem" : "2.5rem",
            },
          }}
        />
      )}
      {(formFields?.[item?.name]?.error ||
        formFields?.[item?.name]?.invalid) && (
        <TextStyled color={Color.error} marginLeft={"1rem"} fontSize={"12px"}>
          {formFields?.[item?.name]?.invalid
            ? item?.invalidMessage
            : item?.errorMessage}
        </TextStyled>
      )}
    </BoxContainer>
  );
}

function CheckBoxInput(props) {
  const { label, value, formFields, setFormFields } = props;

  function handleCheck() {
    setFormFields({
      ...formFields,
      services: {
        value: formFields?.services?.value?.includes(value)
          ? formFields?.services?.value?.filter((item) => item !== value)
          : [...formFields?.services?.value, value],
        error: false,
      },
    });
  }

  const isChecked = formFields?.services?.value?.includes(value);

  return (
    <BoxContainer
      alignItems="center"
      padding="8px 12px"
      width="auto"
      borderRadius="4px"
      justifyContent="space-between"
      border={`1px solid ${Color.grey}`}
      onClick={handleCheck}
      sx={{
        backgroundColor: isChecked ? Color.lightHiredlyPurple : "transparent",
        border: `1px solid ${isChecked ? Color.hiredlyPurple : Color.grey}`,
        cursor: "pointer",
        ":hover": {
          border: !isChecked && `1px solid ${Color.black}`,
        },
      }}
    >
      <TextStyled fontSize="0.875rem">{label}</TextStyled>
      <CheckboxStyled
        icon={<UncheckedIcon />}
        onChange={handleCheck} // Keep the checkbox functional
        value={value}
        checked={isChecked}
        checkedIcon={
          <CheckedIcon>
            <CheckStyled />
          </CheckedIcon>
        }
        sx={{
          pointerEvents: "none", // Prevent checkbox from interfering with container clicks
        }}
      />
    </BoxContainer>
  );
}

function DropDownInput(props) {
  const { dropdownList, formFields, setFormFields } = props;
  const [selected, setSelected] = useState("");

  function handleSelect(event) {
    setSelected(event.target.value);
    setFormFields({
      ...formFields,
      heardFrom: {
        value: event.target.value,
        error: false,
      },
    });
  }

  return (
    <SelectStyled
      sx={{ color: !selected && "#AEAEAE" }}
      value={selected}
      onChange={handleSelect}
      displayEmpty
    >
      <OptionStyled value="" disabled>
        Please Select
      </OptionStyled>
      {dropdownList.map((item, index) => (
        <OptionStyled key={index} value={item.value}>
          {item.label}
        </OptionStyled>
      ))}
    </SelectStyled>
  );
}

export default EmployerLandingContactUsDialog;
